import React, { useEffect, useState } from 'react';
import { BellRing, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { api, errMsg } from '../api';
import { Card, SectionTitle, goldBtn, ghostBtn } from '../ui';

const field = 'h-10 px-3 text-sm bg-transparent border border-[#1F242C] rounded-sm text-[#E5E5E5] outline-none focus:border-[#D4AF37] w-full min-w-0';

function History({ items }) {
  if (!items?.length) return <p className="text-xs text-[#5A5F67] mt-4" data-testid="alert-history-empty">Nessun alert inviato finora.</p>;
  return (
    <div className="mt-5" data-testid="alert-history">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A8F97] mb-2">Ultimi alert inviati</p>
      {items.map((h) => (
        <div key={h.sent_at + h.iso3} className="py-2 border-t border-[#1F242C] text-xs">
          <span className="text-[#F2F2F2]">{h.country}</span> · <span className="text-[#8A8F97]">{new Date(h.sent_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
          <p className="text-[#8A8F97]">{h.changes.map((c) => `${c.name}: ${c.from}→${c.to}`).join(' · ')}</p>
          {h.travelers?.length > 0 && <p className="text-[#5A5F67]">Avvisati anche: {h.travelers.join(', ')}</p>}
          {h.sms?.length > 0 && <p className="text-[#5A5F67]">SMS: {h.sms.map((s) => `${s.phone} (${s.status})`).join(', ')}</p>}
        </div>
      ))}
    </div>
  );
}

function SmsSender({ d, setD }) {
  const opts = [['bond', 'BOND'], ['company', d.company_sender || 'Nome azienda']];
  return (
    <div className="mt-6 max-w-xl" data-testid="alert-sms">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A8F97] mb-2">SMS al viaggiatore · mittente</p>
      <div className="flex flex-wrap gap-2">
        {opts.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setD({ ...d, sms_sender: k })} aria-pressed={d.sms_sender === k}
            className={`h-9 px-4 text-sm rounded-sm border ${d.sms_sender === k ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#1F242C] text-[#C9CCD1]'}`} data-testid={`alert-sms-sender-${k}`}>{label}</button>
        ))}
      </div>
      <p className="text-xs text-[#5A5F67] mt-2" data-testid="alert-sms-note">
        L'SMS parte se nella trasferta è indicato il cellulare del viaggiatore. Il mittente può contenere al massimo 11 caratteri. {d.sms_price_cents > 0 ? `Costo: ${(d.sms_price_cents / 100).toFixed(2)} € per SMS, scalato dal plafond.` : 'SMS inclusi nel servizio.'}
        {d.sms_mock && ' Invio SMS in fase di attivazione: per ora i messaggi vengono registrati ma non consegnati.'}
      </p>
    </div>
  );
}

export default function TravelAlertPreference() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/travel/alerts').then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const setEmail = (i, v) => setD({ ...d, emails: d.emails.map((e, j) => (j === i ? v : e)) });
  const save = async () => {
    setBusy(true);
    try { await api.put('/travel/alerts', { enabled: d.enabled, sms_sender: d.sms_sender, emails: d.emails.map((e) => e.trim()).filter(Boolean) }); toast.success('Impostazioni alert salvate'); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-8 mt-4" data-testid="travel-alert-preference">
      <SectionTitle>Alert Travel Security</SectionTitle>
      <div className="flex items-start gap-4 mb-5">
        <BellRing className="text-[#D4AF37] shrink-0 mt-0.5" size={20} />
        <p className="text-sm text-[#8A8F97] flex-1">Ricevi un'email quando una delle 8 fonti istituzionali alza il livello di rischio di un Paese in cui hai trasferte in corso o programmate. Il controllo avviene ogni ora. Anche il dipendente in trasferta riceve un avviso personale, se la sua email è indicata nella trasferta.</p>
        <Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} data-testid="alert-enabled-switch" />
      </div>
      <div className="space-y-2 max-w-xl">
        {d.emails.map((e, i) => (
          <div key={i} className="flex gap-2">
            <input type="email" value={e} onChange={(ev) => setEmail(i, ev.target.value)} placeholder="nome@azienda.it" className={field} data-testid={`alert-email-${i}`} />
            {d.emails.length > 1 && <button type="button" onClick={() => setD({ ...d, emails: d.emails.filter((_, j) => j !== i) })} className={`${ghostBtn} h-10 w-10 grid place-items-center shrink-0`} aria-label="Rimuovi" data-testid={`alert-email-remove-${i}`}><X size={14} /></button>}
          </div>
        ))}
        <div className="flex flex-wrap gap-2 pt-2">
          {d.emails.length < 5 && <button type="button" onClick={() => setD({ ...d, emails: [...d.emails, ''] })} className={`${ghostBtn} h-10 px-4 text-sm inline-flex items-center gap-2`} data-testid="alert-email-add"><Plus size={14} />Aggiungi indirizzo</button>}
          <button type="button" onClick={save} disabled={busy} className={`${goldBtn} h-10 px-5 text-sm`} data-testid="alert-save">Salva</button>
        </div>
        <p className="text-xs text-[#5A5F67]">Fino a 5 indirizzi. Gli alert partono solo quando il livello sale.</p>
      </div>
      <SmsSender d={d} setD={setD} />
      <History items={d.history} />
    </Card>
  );
}
